/**
 * Represents a payment link for collecting payments (matches backend model)
 */
export interface PaymentLink {
  /** Unique identifier for the payment link */
  id: string
  /** Account ID this payment link belongs to */
  accountId?: number
  /** Display name for the payment link */
  name: string
  /** Description of the payment link */
  description?: string
  /** Amount in cents (for fixed amount links) */
  amount?: number
  /** Currency code (ISO 4217) */
  currency: string
  /** Checkout configuration ID */
  configId?: string
  /** Current status of the payment link */
  status?: "active" | "inactive" | "blocked" | "archived" | "expired" | "draft"
  /** Short code for the payment link URL */
  shortCode?: string
  /** Expiration timestamp */
  expiresAt?: string
  /** Maximum number of uses */
  maxUses?: number
  /** Current usage count */
  currentUses?: number
  /** Total revenue generated */
  totalRevenue?: number
  /** Additional metadata as JSON string */
  metadata?: string
  /** Success redirect URL */
  successUrl?: string
  /** Cancel redirect URL */
  cancelUrl?: string
  // Checkout config properties
  email?: string
  primaryColor?: string
  backgroundColor?: string
  borderStyle?: string
  allowBillingAddress?: boolean
  allowSaveCard?: boolean
  allowTaxSupport?: boolean
  taxPercentage?: number
  allowContactInformation?: boolean
  allowAddressAutocomplete?: boolean
  allowTermsAccepted?: boolean
  allowShippingDetails?: boolean
  allowQuantityChange?: boolean
  allowPhoneNumber?: boolean
  cartItems?: string
  termsUrl?: string
  privacyUrl?: string
  // Payment link configuration
  linkType?: 'cause-service' | 'products-subscriptions'
  paymentPageImageUrl?: string
  afterPaymentImageUrl?: string
  minimumAmount?: number
  maximumAmount?: number
  presetAmounts?: string
  afterPaymentBehavior?: 'confirmation' | 'redirect' | 'invoice'
  requirePhoneNumber?: boolean
  paymentDescriptor?: string
  // Analytics
  viewsCount?: number
  conversionsCount?: number
  lastUsedAt?: string
  /** Creation timestamp */
  created?: string
  /** Last updated timestamp */
  updated?: string

  // Legacy/display properties (used by analytics components)
  /** Price for display purposes */
  price?: number
  /** Type of payment link */
  type?: "fixed" | "donation" | "subscription" | "one-time"
  /** Revenue in last 30 days */
  revenue30d?: number
  /** Success rate percentage */
  successRate?: number
  /** Deactivation timestamp */
  deactivated?: Date
  /** URL of the payment link */
  url?: string
  /** @deprecated Use viewsCount. Total number of views (used by analytics) */
  views?: number
  /** @deprecated Use conversionsCount. Total number of conversions (used by analytics) */
  conversions?: number
  /** @deprecated Use lastUsedAt. Last usage timestamp (used by analytics) */
  lastUsed?: Date
}

/**
 * Form data structure for creating/editing payment links
 * Based on Figma design specifications
 */
export interface PaymentLinkFormData {
  // Step 1: Payment Details
  paymentFor: 'cause-service' | 'products-subscriptions' | ''
  
  // Payment page tab fields
  paymentPage: {
    title: string
    description: string
    image?: string
    shortCode?: string
  }
  
  // After payment tab fields
  afterPayment: {
    title: string
    description: string
    image?: string
  }
  
  // Currency (separate field in Figma)
  currency: string
  
  // Preset amounts toggle and configuration
  suggestPresetAmounts: boolean
  presetAmounts: string[]
  
  // Amount limits toggle and configuration
  setAmountLimits: boolean
  minimumAmount: string
  maximumAmount: string
  
  // Payment descriptor
  paymentDescriptor: string
  
  // For products/subscriptions type
  selectedProducts: Array<{
    id: string
    name: string
    description?: string
    price: number
    image?: string
    quantity: number
    allowQuantityChange: boolean
  }>
  
  // Collection requirements
  requireShippingAddress: boolean
  requirePhoneNumber: boolean

  // Products flow options
  collectTax: boolean
  collectCustomerName: boolean
  collectCustomerNameOptional: boolean
  collectBusinessName: boolean
  collectBusinessNameOptional: boolean
  billingAddressOnly: boolean
  addCustomFields: boolean
  allowPromotionCodes: boolean
  requireTermsOfService: boolean
  callToAction: string

  // Products flow advanced options
  setCustomStatementDescriptor: boolean
  allowQuantityChange: boolean
  quantityMin: string
  quantityMax: string

  // Tax configuration
  taxPercentage?: string

  // Terms of service URL
  termsUrl?: string

  // Customization / appearance
  customization: {
    primaryColor: string
    backgroundColor: string
    borderStyle: 'rounded' | 'square' | 'pill'
  }

  // Advanced options (collapsible)
  advanced: {
    enableCustomerDataSaving: boolean
    allowBusinessTaxIds: boolean
    expirationDate?: string
    enableExpirationDate: boolean
    limitLinkUsage: boolean
    maxUsages?: number
  }

  // After payment settings
  afterPaymentBehavior: 'invoice' | 'redirect' | 'confirmation'
  redirectUrl?: string

  // Configuration (implicit from design)
  shortCode: string
  useCustomShortCode: boolean
  status: 'active' | 'inactive' | 'draft'

  // Draft management
  isDraft?: boolean
  lastSaved?: string
}

/**
 * Returns initial form data for payment link creation
 */
export function getInitialPaymentLinkFormData(): PaymentLinkFormData {
  return {
    paymentFor: '',
    paymentPage: {
      title: '',
      description: '',
      image: ''
    },
    afterPayment: {
      title: '',
      description: '',
      image: ''
    },
    currency: 'BGN',
    suggestPresetAmounts: false,
    presetAmounts: [],
    setAmountLimits: false,
    minimumAmount: '',
    maximumAmount: '',
    paymentDescriptor: '',
    selectedProducts: [],
    requireShippingAddress: false,
    requirePhoneNumber: false,
    collectTax: false,
    collectCustomerName: true,
    collectCustomerNameOptional: true,
    collectBusinessName: true,
    collectBusinessNameOptional: true,
    billingAddressOnly: true,
    addCustomFields: false,
    allowPromotionCodes: false,
    requireTermsOfService: false,
    callToAction: 'pay',
    setCustomStatementDescriptor: false,
    allowQuantityChange: false,
    quantityMin: '0',
    quantityMax: '5',
    taxPercentage: undefined,
    termsUrl: undefined,
    customization: {
      primaryColor: '#0B3B45',
      backgroundColor: '#FFFFFF',
      borderStyle: 'rounded',
    },
    advanced: {
      enableCustomerDataSaving: false,
      allowBusinessTaxIds: false,
      expirationDate: '',
      enableExpirationDate: false,
      limitLinkUsage: false,
      maxUsages: undefined
    },
    afterPaymentBehavior: 'confirmation',
    redirectUrl: '',
    shortCode: '',
    useCustomShortCode: false,
    status: 'active',
    isDraft: false,
    lastSaved: undefined
  }
}

/**
 * Transforms PaymentLink to PaymentLinkFormData for editing
 */
export function transformPaymentLinkToFormData(paymentLink: PaymentLink): PaymentLinkFormData {
  // Parse metadata to get linkType and details
  interface PaymentLinkMetadata {
    paymentFor?: string
    paymentPage?: { title?: string; description?: string; image?: string }
    afterPayment?: { title?: string; description?: string; image?: string }
    suggestPresetAmounts?: boolean
    presetAmounts?: string[]
    setAmountLimits?: boolean
    minimumAmount?: string
    maximumAmount?: string
    paymentDescriptor?: string
    requirePhoneNumber?: boolean
    collectTax?: boolean
    collectCustomerName?: boolean
    collectCustomerNameOptional?: boolean
    collectBusinessName?: boolean
    collectBusinessNameOptional?: boolean
    billingAddressOnly?: boolean
    addCustomFields?: boolean
    allowPromotionCodes?: boolean
    requireTermsOfService?: boolean
    callToAction?: string
    setCustomStatementDescriptor?: boolean
    allowQuantityChange?: boolean
    quantityMin?: string
    quantityMax?: string
    afterPaymentBehavior?: 'invoice' | 'redirect' | 'confirmation'
  }
  let metadata: PaymentLinkMetadata = {}
  try {
    metadata = paymentLink.metadata ? JSON.parse(paymentLink.metadata) : {}
  } catch (e) {
    console.error('Failed to parse metadata:', e)
  }

  // Prefer dedicated columns over metadata
  const paymentFor = paymentLink.linkType || metadata.paymentFor || 'cause-service'

  // Parse cart items
  let products: Record<string, unknown>[] = []
  try {
    products = paymentLink.cartItems ? JSON.parse(paymentLink.cartItems) : []
  } catch (e) {
    console.error('Failed to parse cart items:', e)
  }

  // Parse preset amounts
  let presetAmounts: string[] = []
  try {
    if (paymentLink.presetAmounts) {
      presetAmounts = JSON.parse(paymentLink.presetAmounts)
    } else if (metadata.presetAmounts) {
      presetAmounts = metadata.presetAmounts
    }
  } catch (e) {
    console.error('Failed to parse preset amounts:', e)
  }

  // Convert minimum/maximum amounts from cents to currency units
  const minimumAmount = paymentLink.minimumAmount
    ? (paymentLink.minimumAmount / 100).toString()
    : metadata.minimumAmount || ''
  const maximumAmount = paymentLink.maximumAmount
    ? (paymentLink.maximumAmount / 100).toString()
    : metadata.maximumAmount || ''

  return {
    paymentFor: paymentFor as 'cause-service' | 'products-subscriptions' | '',
    paymentPage: {
      title: metadata.paymentPage?.title || paymentLink.name || '',
      description: metadata.paymentPage?.description || paymentLink.description || '',
      image: paymentLink.paymentPageImageUrl || metadata.paymentPage?.image || ''
    },
    afterPayment: {
      title: metadata.afterPayment?.title || '',
      description: metadata.afterPayment?.description || '',
      image: paymentLink.afterPaymentImageUrl || metadata.afterPayment?.image || ''
    },
    currency: paymentLink.currency || 'BGN',
    suggestPresetAmounts: metadata.suggestPresetAmounts || false,
    presetAmounts: presetAmounts,
    setAmountLimits: metadata.setAmountLimits || !!(paymentLink.minimumAmount || paymentLink.maximumAmount),
    minimumAmount: minimumAmount,
    maximumAmount: maximumAmount,
    paymentDescriptor: paymentLink.paymentDescriptor || metadata.paymentDescriptor || '',
    selectedProducts: products.map((p) => ({
      id: (p.id as string) || `product_${Date.now()}`,
      name: (p.name as string) || '',
      description: (p.description as string) || '',
      price: (p.price as number) || 0,
      image: (p.image as string) || '',
      quantity: (p.quantity as number) || 1,
      allowQuantityChange: (p.allowQuantityChange as boolean) || false
    })),
    requireShippingAddress: paymentLink.allowShippingDetails ?? false,
    requirePhoneNumber: paymentLink.requirePhoneNumber ?? metadata.requirePhoneNumber ?? false,
    collectTax: metadata.collectTax ?? false,
    collectCustomerName: metadata.collectCustomerName ?? true,
    collectCustomerNameOptional: metadata.collectCustomerNameOptional ?? true,
    collectBusinessName: metadata.collectBusinessName ?? true,
    collectBusinessNameOptional: metadata.collectBusinessNameOptional ?? true,
    billingAddressOnly: metadata.billingAddressOnly ?? true,
    addCustomFields: metadata.addCustomFields ?? false,
    allowPromotionCodes: metadata.allowPromotionCodes ?? false,
    requireTermsOfService: metadata.requireTermsOfService ?? false,
    callToAction: metadata.callToAction ?? 'pay',
    setCustomStatementDescriptor: !!(paymentLink.paymentDescriptor || metadata.setCustomStatementDescriptor),
    allowQuantityChange: metadata.allowQuantityChange ?? false,
    quantityMin: metadata.quantityMin ?? '0',
    quantityMax: metadata.quantityMax ?? '5',
    taxPercentage: paymentLink.taxPercentage != null ? String(paymentLink.taxPercentage) : undefined,
    termsUrl: paymentLink.termsUrl || undefined,
    customization: {
      primaryColor: paymentLink.primaryColor || '#0B3B45',
      backgroundColor: paymentLink.backgroundColor || '#FFFFFF',
      borderStyle: (paymentLink.borderStyle as 'rounded' | 'square' | 'pill') || 'rounded',
    },
    advanced: {
      enableCustomerDataSaving: paymentLink.allowSaveCard ?? false,
      allowBusinessTaxIds: paymentLink.allowTaxSupport ?? false,
      expirationDate: paymentLink.expiresAt || '',
      enableExpirationDate: !!paymentLink.expiresAt,
      limitLinkUsage: !!paymentLink.maxUses,
      maxUsages: paymentLink.maxUses
    },
    afterPaymentBehavior: paymentLink.afterPaymentBehavior || metadata.afterPaymentBehavior || 'confirmation',
    redirectUrl: paymentLink.successUrl || '',
    shortCode: paymentLink.shortCode || '',
    useCustomShortCode: !!paymentLink.shortCode,
    status: (paymentLink.status as 'active' | 'inactive' | 'draft') || 'active',
    isDraft: paymentLink.status === 'draft',
    lastSaved: paymentLink.updated || paymentLink.created
  }
}

/**
 * Transforms PaymentLinkFormData to PaymentLink API format
 */
export function transformFormDataToPaymentLink(formData: PaymentLinkFormData): Partial<PaymentLink> {
  // Calculate amount in cents if products are selected
  let amount: number | undefined
  if (formData.selectedProducts.length > 0) {
    amount = formData.selectedProducts[0].price
  }

  // Build cart items from selected products
  let cartItems: string | undefined
  if (formData.selectedProducts && formData.selectedProducts.length > 0) {
    cartItems = JSON.stringify(formData.selectedProducts.map(product => ({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      quantity: product.quantity,
      allowQuantityChange: product.allowQuantityChange,
      image: product.image
    })))
  }

  // Build metadata object (only for display/UI metadata)
  const metadata = {
    paymentPage: formData.paymentPage,
    afterPayment: formData.afterPayment,
    suggestPresetAmounts: formData.suggestPresetAmounts,
    setAmountLimits: formData.setAmountLimits,
    collectTax: formData.collectTax,
    collectCustomerName: formData.collectCustomerName,
    collectCustomerNameOptional: formData.collectCustomerNameOptional,
    collectBusinessName: formData.collectBusinessName,
    collectBusinessNameOptional: formData.collectBusinessNameOptional,
    billingAddressOnly: formData.billingAddressOnly,
    addCustomFields: formData.addCustomFields,
    allowPromotionCodes: formData.allowPromotionCodes,
    requireTermsOfService: formData.requireTermsOfService,
    callToAction: formData.callToAction,
    setCustomStatementDescriptor: formData.setCustomStatementDescriptor,
    allowQuantityChange: formData.allowQuantityChange,
    quantityMin: formData.quantityMin,
    quantityMax: formData.quantityMax
  }

  const paymentLink: Partial<PaymentLink> = {
    // Basic Info
    name: formData.paymentPage.title || 'Payment Link',
    description: formData.paymentPage.description,
    amount: amount,
    currency: formData.currency,

    // Configuration
    status: formData.status,
    shortCode: formData.shortCode, // Required field

    // Metadata (only for UI/display data)
    metadata: JSON.stringify(metadata),

    // Payment link configuration (dedicated columns)
    linkType: (formData.paymentFor || 'cause-service') as PaymentLink['linkType'],
    paymentPageImageUrl: formData.paymentPage.image || undefined,
    afterPaymentImageUrl: formData.afterPayment.image || undefined,
    afterPaymentBehavior: formData.afterPaymentBehavior || 'confirmation',
    requirePhoneNumber: formData.requirePhoneNumber || false,
    paymentDescriptor: formData.paymentDescriptor || undefined,

    // Payment Features (Checkout Config Properties)
    allowSaveCard: formData.advanced.enableCustomerDataSaving,
    allowShippingDetails: formData.requireShippingAddress,
    allowTaxSupport: formData.advanced.allowBusinessTaxIds,
    allowContactInformation: true, // Default enabled
    allowAddressAutocomplete: false, // Default disabled
    allowTermsAccepted: false, // TODO: Add to form if needed
    allowPhoneNumber: formData.requirePhoneNumber,
    allowQuantityChange: formData.selectedProducts.some(p => p.allowQuantityChange),

    // Styling (from customization)
    primaryColor: formData.customization.primaryColor || '#0B3B45',
    backgroundColor: formData.customization.backgroundColor || '#FFFFFF',
    borderStyle: formData.customization.borderStyle || 'rounded',

    // Terms URL
    termsUrl: formData.termsUrl || undefined
  }

  // Preset amounts (dedicated column)
  if (formData.suggestPresetAmounts && formData.presetAmounts && formData.presetAmounts.length > 0) {
    paymentLink.presetAmounts = JSON.stringify(formData.presetAmounts)
  }

  // Amount limits (dedicated columns)
  if (formData.setAmountLimits) {
    if (formData.minimumAmount && formData.minimumAmount.trim()) {
      paymentLink.minimumAmount = Math.round(parseFloat(formData.minimumAmount) * 100) // Convert to cents
    }
    if (formData.maximumAmount && formData.maximumAmount.trim()) {
      paymentLink.maximumAmount = Math.round(parseFloat(formData.maximumAmount) * 100) // Convert to cents
    }
  }

  // Conditionally add optional fields only if they have valid values
  if (formData.advanced.enableExpirationDate && formData.advanced.expirationDate?.trim()) {
    // Backend expects LocalDateTime in ISO 8601 format (YYYY-MM-DDTHH:mm:ss)
    // If only date is provided, append end of day time
    let expiresAt = formData.advanced.expirationDate.trim()
    if (!expiresAt.includes('T')) {
      expiresAt = `${expiresAt}T23:59:59`
    }
    paymentLink.expiresAt = expiresAt
  } else if (!formData.advanced.enableExpirationDate) {
    // Set default expiration to 1 year from now if not explicitly set
    const oneYearFromNow = new Date()
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)
    const year = oneYearFromNow.getFullYear()
    const month = String(oneYearFromNow.getMonth() + 1).padStart(2, '0')
    const day = String(oneYearFromNow.getDate()).padStart(2, '0')
    paymentLink.expiresAt = `${year}-${month}-${day}T23:59:59`
  }

  if (formData.advanced.limitLinkUsage && formData.advanced.maxUsages) {
    paymentLink.maxUses = formData.advanced.maxUsages
  }

  if (formData.redirectUrl?.trim()) {
    paymentLink.successUrl = formData.redirectUrl
  }

  if (formData.advanced.allowBusinessTaxIds && formData.taxPercentage) {
    paymentLink.taxPercentage = parseFloat(formData.taxPercentage) || 0
  }

  if (cartItems) {
    paymentLink.cartItems = cartItems
  }

  return paymentLink
}